import { useState } from "react";
import { Search, Sparkles, Zap } from "lucide-react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { DorkCard } from "@/components/DorkCard";
import { AdvancedOptions } from "@/components/AdvancedOptions";
import { PresetTemplates } from "@/components/PresetTemplates";
import { DorkHistory } from "@/components/DorkHistory";
import { ExportOptions } from "@/components/ExportOptions";
import { LoadingState } from "@/components/LoadingState";
import { OperatorsGuide } from "@/components/OperatorsGuide";
import { SavedDorksLibrary } from "@/components/SavedDorksLibrary";
import { DorkOptimizer } from "@/components/DorkOptimizer";
import { DorkExplainer } from "@/components/DorkExplainer";
import { DorkValidator } from "@/components/DorkValidator";
import { DorkCatalog } from "@/components/DorkCatalog";
import { SuggestedDorks } from "@/components/SuggestedDorks";
import { useDorkHistory } from "@/hooks/useDorkHistory";
import { generateDorks } from "@/services/dorkService";
import { DorkGenerationOptions, GeneratedDork, PresetTemplate, DorkHistoryItem } from "@/types/dork";
import { toast } from "@/hooks/use-toast";
import { useLocale } from "@/contexts/LocaleContext";
import { usePlan } from "@/hooks/usePlan";
import { Link } from "react-router-dom";
import { Lock } from "lucide-react";

const Index = () => {
  const { t } = useLocale();
  const { isFreeTier } = usePlan();
  const [keyword, setKeyword] = useState("");
  const [options, setOptions] = useState<DorkGenerationOptions>({
    keyword: "",
    sensitivityLevel: "medium",
  });
  const [activePreset, setActivePreset] = useState<PresetTemplate | null>(null);
  const [dorks, setDorks] = useState<GeneratedDork[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const { history, addToHistory, clearHistory } = useDorkHistory();

  const handlePresetSelect = (presetOptions: Partial<DorkGenerationOptions>, template: PresetTemplate) => {
    if (activePreset === template) {
      setActivePreset(null);
      setOptions({ ...options, sensitivityLevel: "medium", fileTypes: undefined, domains: undefined });
    } else {
      setActivePreset(template);
      setOptions({ ...options, ...presetOptions });
    }
  };

  const handleGenerate = async () => {
    if (!keyword.trim()) {
      toast({
        title: t("toast.keyword_required"),
        description: t("toast.keyword_required_desc"),
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);
    setDorks([]);

    try {
      const result = await generateDorks({ ...options, keyword: keyword.trim() });
      setDorks(result.dorks);
      addToHistory(keyword.trim(), result.dorks, { ...options, keyword: keyword.trim() });
      
      toast({
        title: t("toast.generated"),
        description: t("toast.generated_desc", { count: String(result.dorks.length), type: result.inputType }),
      });
    } catch (error) {
      console.error("Generation error:", error);
      toast({
        title: t("toast.generation_failed"),
        description: error instanceof Error ? error.message : t("toast.generation_failed"),
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleHistorySelect = (item: DorkHistoryItem) => {
    setKeyword(item.keyword);
    setOptions(item.options);
    setDorks(item.dorks);
    
    const presetMatch = item.options.sensitivityLevel === "high" 
      ? "security" 
      : item.options.sensitivityLevel === "low" 
        ? "academic" 
        : null;
    setActivePreset(presetMatch);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleGenerate();
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-gradient-dark">
      <div className="fixed inset-0 bg-grid-pattern opacity-30 pointer-events-none" />
      <div className="fixed inset-0 bg-gradient-radial from-primary/5 via-transparent to-transparent pointer-events-none" />
      
      <Header />

      <main className="flex-1 container mx-auto px-4 py-8 relative z-10">
        {/* Hero Section */}
        <section className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 mb-6">
            <Sparkles className="w-4 h-4 text-primary" />
            <span className="text-sm font-medium text-primary">{t("index.badge")}</span>
          </div>
          
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4">
            <span className="text-gradient-cyber inline-block">{t("index.hero_title_1")}</span>
            <br />
            <span className="text-foreground">{t("index.hero_title_2")}</span>
          </h2>
          
          <p className="text-muted-foreground max-w-2xl mx-auto text-lg">
            {t("index.hero_subtitle")}
          </p>
        </section>

        {/* Search Section */}
        <section className="max-w-3xl mx-auto mb-8 space-y-4">
          <div className="relative">
            <div className="absolute inset-0 bg-gradient-to-r from-primary/20 to-secondary/20 rounded-2xl blur-xl opacity-50" />
            <div className="relative glass-card p-2">
              <div className="flex items-center gap-2">
                <div className="flex-1 relative">
                  <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                  <input
                    type="text"
                    value={keyword}
                    onChange={(e) => setKeyword(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder={t("index.placeholder")}
                    className="w-full pl-12 pr-4 py-4 bg-transparent text-foreground text-lg placeholder:text-muted-foreground focus:outline-none"
                  />
                </div>
                <button
                  onClick={handleGenerate}
                  disabled={isLoading}
                  className="flex items-center gap-2 px-6 py-4 rounded-xl bg-gradient-to-r from-primary to-secondary text-primary-foreground font-semibold transition-all duration-300 hover:shadow-neon disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Zap className="w-5 h-5" />
                  <span className="hidden sm:inline">{t("index.generate_btn")}</span>
                </button>
              </div>
            </div>
          </div>

          {!isFreeTier && <SuggestedDorks keyword={keyword} onSelectDork={setKeyword} />}
          <DorkValidator query={keyword} onQueryChange={setKeyword} />
          {!isFreeTier && (
            <>
              <PresetTemplates onSelect={handlePresetSelect} activeTemplate={activePreset} />
              <AdvancedOptions options={options} onOptionsChange={setOptions} />
              <OperatorsGuide />

              <div className="grid md:grid-cols-2 gap-4">
                <DorkOptimizer />
                <DorkExplainer />
              </div>

              <DorkCatalog onSelectDork={setKeyword} />
              <SavedDorksLibrary />

              <DorkHistory
                history={history}
                onSelectHistory={handleHistorySelect}
                onClearHistory={clearHistory}
              />
            </>
          )}

          {isFreeTier && (
            <div className="glass-card p-6 text-center space-y-3">
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-primary/10 border border-primary/20">
                <Lock className="w-6 h-6 text-primary" />
              </div>
              <h3 className="text-lg font-semibold text-foreground">Free tier: 1 search per day</h3>
              <p className="text-sm text-muted-foreground max-w-md mx-auto">
                Advanced options, presets, catalog, optimizer, explainer, history, exports and saved collections are available on paid plans.
              </p>
              <Link
                to="/pricing"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-gradient-to-r from-primary to-secondary text-primary-foreground font-semibold transition-all duration-300 hover:shadow-neon"
              >
                Upgrade to unlock
              </Link>
            </div>
          )}
        </section>

        {/* Results Section */}
        <section className="max-w-5xl mx-auto">
          {isLoading ? (
            <LoadingState />
          ) : dorks.length > 0 ? (
            <div className="space-y-6">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div>
                  <h3 className="text-xl font-semibold text-foreground">
                    {t("index.results_title")}
                  </h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    {t("index.results_count", { count: String(dorks.length), keyword })}
                  </p>
                </div>
                {!isFreeTier && <ExportOptions dorks={dorks} keyword={keyword} />}
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                {dorks.map((dork, index) => (
                  <DorkCard key={dork.id} dork={dork} index={index} />
                ))}
              </div>
            </div>
          ) : (
            <div className="text-center py-16">
              <div className="w-20 h-20 rounded-2xl bg-muted/30 flex items-center justify-center mx-auto mb-4">
                <Search className="w-10 h-10 text-muted-foreground/50" />
              </div>
              <h3 className="text-lg font-medium text-muted-foreground mb-2">
                {t("index.empty_title")}
              </h3>
              <p className="text-sm text-muted-foreground/70">
                {t("index.empty_subtitle")}
              </p>
            </div>
          )}
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default Index;
